
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSettingsLayout from '@/components/admin/AdminSettingsLayout';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search, UserPlus, Users, RefreshCw, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { sendEmailNotification } from '@/utils/emailNotificationService';

export interface PendingUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  createdAt: string;
  selected?: boolean;
}

const mockPendingUsers: PendingUser[] = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '555-123-4567',
    source: 'Shopify',
    createdAt: '2025-04-15T10:30:00Z'
  },
  {
    id: '2',
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    phone: '555-987-6543',
    source: 'WooCommerce',
    createdAt: '2025-04-14T14:20:00Z'
  },
  {
    id: '3',
    name: 'Michael Johnson',
    email: 'michael.j@example.com',
    phone: '555-555-5555',
    source: 'BigCommerce',
    createdAt: '2025-04-13T09:15:00Z'
  }
];

const AdminAutoUsers = () => {
  const navigate = useNavigate();
  const { isLoading, user, handleLogout } = useAdminSettings();
  const { toast } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  
  useEffect(() => {
    loadPendingUsers();
  }, []);
  
  const loadPendingUsers = () => {
    setIsLoadingUsers(true);
    
    // In a real app, this would be an API call
    setTimeout(() => {
      // Load from localStorage if available, otherwise use mock data
      const storedUsers = localStorage.getItem('pendingUsers');
      if (storedUsers) {
        setPendingUsers(JSON.parse(storedUsers));
      } else {
        setPendingUsers(mockPendingUsers);
      }
      setIsLoadingUsers(false);
    }, 800);
  };
  
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setPendingUsers(pendingUsers.map(user => ({
      ...user,
      selected: checked
    })));
  };
  
  const handleSelectUser = (id: string, checked: boolean) => {
    setPendingUsers(pendingUsers.map(user => 
      user.id === id ? { ...user, selected: checked } : user
    ));
    
    // Update selectAll state based on if all users are selected
    const allSelected = pendingUsers
      .filter(user => user.id !== id) // Exclude current user
      .every(user => user.selected) && checked; // Check if all others and current are selected
      
    setSelectAll(allSelected);
  };
  
  const getSelectedUsers = () => {
    return pendingUsers.filter(user => user.selected);
  };
  
  const approveSelectedUsers = async () => {
    const selectedUsers = getSelectedUsers();
    
    if (selectedUsers.length === 0) {
      toast({
        title: "No users selected",
        description: "Please select at least one user to approve.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoadingUsers(true);
    
    try {
      // Get email template from localStorage or use default
      let emailTemplate;
      try {
        const storedTemplate = localStorage.getItem('autoUserEmailTemplate');
        emailTemplate = storedTemplate ? JSON.parse(storedTemplate) : null;
      } catch (error) {
        console.error('Error loading email template:', error);
        emailTemplate = null;
      }
      
      // Send welcome emails and remove approved users
      const userIds = selectedUsers.map(user => user.id);
      
      // In a real app, this would be an API call to approve users and send emails
      for (const user of selectedUsers) {
        // Send welcome email
        await sendEmailNotification(
          user.email, 
          emailTemplate?.subject || 'Welcome to our Referral Program!',
          emailTemplate?.content || `Welcome ${user.name}! You're now part of our referral program.`
        );
      }
      
      // Remove approved users from the list
      const updatedPendingUsers = pendingUsers.filter(user => !userIds.includes(user.id));
      setPendingUsers(updatedPendingUsers);
      
      // Save updated list to localStorage
      localStorage.setItem('pendingUsers', JSON.stringify(updatedPendingUsers));
      
      toast({
        title: "Users approved",
        description: `Successfully approved ${selectedUsers.length} users and sent welcome emails.`,
      });
      
      // Reset selection
      setSelectAll(false);
    } catch (error) {
      console.error('Error approving users:', error);
      toast({
        title: "Error",
        description: "Failed to approve selected users. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };
  
  const deleteSelectedUsers = () => {
    const selectedUsers = getSelectedUsers();
    
    if (selectedUsers.length === 0) {
      toast({
        title: "No users selected",
        description: "Please select at least one user to delete.",
        variant: "destructive"
      });
      return;
    }
    
    // Remove selected users from the list
    const userIds = selectedUsers.map(user => user.id);
    const updatedPendingUsers = pendingUsers.filter(user => !userIds.includes(user.id));
    
    // Update state and localStorage
    setPendingUsers(updatedPendingUsers);
    localStorage.setItem('pendingUsers', JSON.stringify(updatedPendingUsers));
    
    // Reset selection
    setSelectAll(false);
    
    toast({
      title: "Users deleted",
      description: `Successfully deleted ${selectedUsers.length} users from the pending list.`,
    });
  };
  
  const filteredUsers = pendingUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone.includes(searchTerm) ||
    user.source.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  return (
    <AdminSettingsLayout
      isLoading={isLoading}
      userRole={user?.role}
      onLogout={handleLogout}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/settings')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>
        
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-2" />
              Auto User Management
            </h1>
            <p className="text-gray-600">
              Manage and approve users automatically imported from e-commerce platforms.
            </p>
          </div>
          <Button onClick={loadPendingUsers} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        <div className="bg-white shadow-sm rounded-lg">
          <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-80"
              />
            </div>
            <div className="flex space-x-2 w-full sm:w-auto">
              <Button 
                onClick={approveSelectedUsers} 
                disabled={getSelectedUsers().length === 0 || isLoadingUsers}
                className="flex-1 sm:flex-none"
              >
                <Check className="h-4 w-4 mr-2" />
                <span>Approve Selected</span>
              </Button>
              <Button 
                onClick={deleteSelectedUsers} 
                variant="outline" 
                disabled={getSelectedUsers().length === 0 || isLoadingUsers}
                className="flex-1 sm:flex-none"
              >
                <X className="h-4 w-4 mr-2" />
                <span>Delete Selected</span>
              </Button>
            </div>
          </div>
          
          {isLoadingUsers ? (
            <div className="flex justify-center items-center p-12">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-12 text-center">
              <UserPlus className="h-12 w-12 mx-auto text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No pending users</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'No users match your search criteria.' : 'There are no pending users to approve at this time.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectAll} 
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Checkbox 
                          checked={user.selected || false} 
                          onCheckedChange={(checked) => handleSelectUser(user.id, checked === true)}
                          aria-label={`Select ${user.name}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {user.source}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{filteredUsers.length}</span> of{" "}
              <span className="font-medium">{pendingUsers.length}</span> pending users
            </div>
          </div>
        </div>
      </div>
    </AdminSettingsLayout>
  );
};

export default AdminAutoUsers;
